<template>
  <PortalWidget
    :widget="widgetTitle"
  >
    <ValidationObserver
      v-if="!isLoading"
      ref="formValidation"
      v-slot="{ handleSubmit, invalid }"
      slim
    >
      <form
        role="form"
        class="form-horizontal"
        name="billingSettingsRechargeForm"
        @submit.prevent="handleSubmit(onSaveRechargeSettings)"
      >
        <Field
          :label="$t('ACCOUNT_BILLING_SETTINGS_RECHARGE')"
          :has-tooltip="true"
          :tooltip-text="$t('ACCOUNT_BILLING_SETTINGS_RECHARGE_TT')"
        >
          <template #element>
            <div class="p-l-0 col-sm-5">
              <nzToggle
                v-model="localBillingSettings.autoRecharge"
                data-testid="account-recharge-settings-toggle"
                @onToggle="onToggleRechargeSettings"
              />
            </div>
          </template>
        </Field>
        <template v-if="localBillingSettings.autoRecharge">
          <Field
            :label="$t('ACCOUNT_BILLING_SETTINGS_RECHARGE_BELOW')"
            :has-tooltip="true"
            :tooltip-text="$t('ACCOUNT_BILLING_SETTINGS_RECHARGE_BELOW_TT')"
          >
            <template #element>
              <div class="col-sm-2">
                <SearchableDropdown
                  v-model="localBillingSettings.rechargeBalance"
                  :config="rechargeBelowOptionsConfig"
                  :searchable="false"
                  :clearable="false"
                  rules="required|notZero"
                  class="r-form__input"
                  data-testid="account-recharge-balance-options"
                />
              </div>
            </template>
          </Field>
          <Field
            :label="$t('ACCOUNT_BILLING_SETTINGS_RECHARGE_AMOUNT')"
            :has-tooltip="true"
            :tooltip-text="$t('ACCOUNT_BILLING_SETTINGS_RECHARGE_AMOUNT_TT')"
          >
            <template #element>
              <div class="col-sm-2">
                <SearchableDropdown
                  v-model="localBillingSettings.rechargeAmount"
                  :config="rechargeAmountOptionsConfig"
                  :searchable="false"
                  :clearable="false"
                  rules="required|notZero"
                  class="r-form__input"
                  data-testid="account-recharge-amount-options"
                />
              </div>
            </template>
          </Field>
        </template>
        <div
          class="col-sm-9 col-sm-offset-3 m-t-25"
          :class="{'p-l-0': !localBillingSettings.autoRecharge}"
        >
          <PrimaryButton
            :disabled="invalid"
            :show-loader-on-form="isRechargeUpdating"
          >
            <span v-t="'UPDATE'" />
          </PrimaryButton>
        </div>
      </form>
    </ValidationObserver>
    <div v-else>
      <Loader />
      <span v-t="'LOADING'" />
    </div>
  </PortalWidget>
</template>

<script>
import { cloneDeep } from 'lodash';
import messages from '../../locales';
import sharedMessages from '@/core/locales';
import { utilities } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { mapActions, mapGetters } from 'vuex';
import { STATUS_SUCCESS, STATUS_PENDING, STATUS_ERROR } from '@/store/helpers';
import { rechargeBelowOptions, rechargeAmountOptions } from '../../helpers/constants.js';
import Field from '@/core/components/LabelAndElement/LabelAndElement';
import nzToggle from '@/core/components/nzToggle/nzToggle';
import SearchableDropdown from '@/core/components/SearchableDropdown/SearchableDropdown';

const amountSelectConfig = {
  reduce: (option) => option.id,
  label: 'name',
};

export default {
  name: 'RechargeSettings',
  components: {
    Field,
    nzToggle,
    SearchableDropdown
  },
  props: {
    billingSettings: {
      type: Object,
      default: () => ({}),
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      widgetTitle: { title: this.$t('ACCOUNT_BILLING_SETTINGS_RECHARGE_HEADER'), iconClass: 'typcn typcn-refresh' },
      localBillingSettings: {
        autoRecharge: false,
        rechargeBalance: 0,
        rechargeAmount: 0
      }
    };
  },
  computed: {
    ...mapGetters('billingSettingsStore', ['updateBillingSettingsStatus']),
    isRechargeUpdating() {
      return this.updateBillingSettingsStatus.status === STATUS_PENDING;
    },
    rechargeBelowOptionsConfig() {
      return {
        options: rechargeBelowOptions,
        ...amountSelectConfig,
        placeholder: this.$t('ACCOUNT_BILLING_SETTINGS_RECHARGE_BELOW_DEFAULT')
      };
    },
    rechargeAmountOptionsConfig() {
      return {
        options: rechargeAmountOptions,
        ...amountSelectConfig,
        placeholder: this.$t('ACCOUNT_BILLING_SETTINGS_RECHARGE_AMOUNT_DEFAULT')
      };
    },
  },
  watch: {
    billingSettings: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.localBillingSettings = cloneDeep(newValue);
        }
      }
    }
  },
  methods: {
    ...mapActions('billingSettingsStore', ['updateBillingSettings']),
    async onSaveRechargeSettings() {
      if (!this.localBillingSettings.autoRecharge) {
        this.localBillingSettings.rechargeBalance = null;
        this.localBillingSettings.rechargeAmount = null;
      }
      await this.updateBillingSettings(this.localBillingSettings);
      if (this.updateBillingSettingsStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(STATUS_SUCCESS, this.$i18n.t('ACCOUNT_BILLING_SETTINGS_RECHARGE_SUCCESS'));
      }
      if (this.updateBillingSettingsStatus.status === STATUS_ERROR) {
        utilities.showToaster(STATUS_ERROR, this.updateBillingSettingsStatus.error);
      }
    },
    onToggleRechargeSettings() {
      if (!this.localBillingSettings.autoRecharge) {
        this.localBillingSettings.rechargeBalance = 0;
        this.localBillingSettings.rechargeAmount = 0;
      }
    },
  },

  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  }
};
</script>